import React, { useState, useEffect } from 'react';
import { Eye, Edit2, Trash2, Plus, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../axiosInstance';

function CityDetailMain() {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCities = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get("/citydetail/allcities");
        const result = response.data;

        if (result?.data) {
          const formattedCities = result.data.map((item, index) => ({
            id: item._id || index + 1,
            city: item.city.cityname,
            state: item.city.state,
            country: item.city.country,
          }));

          setCities(formattedCities);
        }
      } catch (error) {
        console.error("Error fetching cities:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCities();
  }, []);

  const handleDelete = async (id) => {
    try {
      await axiosInstance.delete(`/citydetail/delete/${id}`);
      setCities(prev => prev.filter(city => city.id !== id));
    } catch (error) {
      console.error("Error deleting city:", error);
    }
  };

  const handleEdit = ({ id, city, state, country }) => {
    navigate(`/management/editCityDetail`, {
      state: { cityname: city, statee: state, country, id }
    });
  };

  const handleView = ({ id, city, state, country }) => {
    navigate(`/management/viewCitydetail`, {
      state: { cityname: city, statee: state, country, id }
    });
  };

  const handleAdd = () => {
    navigate('/management/mainpage', { state: { isAddingNewCity: true } });
  };

  const filteredCities = cities.filter(city =>
    city.city.toLowerCase().includes(search.toLowerCase()) ||
    city.state.toLowerCase().includes(search.toLowerCase()) ||
    city.country.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="bg-gray-900 text-gray-100 p-6" style={{
      overflowY: 'scroll',
      height: '80vh',
      width: '100%',
      scrollbarWidth: 'none',
      msOverflowStyle: 'none'
    }}>
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-1">City Management</h1>
            <p className="text-gray-400">Manage and view all city details</p>
          </div>
        <button
          onClick={handleAdd}
          className="group mt-4 md:mt-0 flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-purple-600  text-white px-5 py-2.5 rounded-xl shadow-lg  transition-transform duration-300 ease-in-out active:scale-95 focus:outline-none focus:ring-2 focus:ring-indigo-300"
        >
          <Plus size={18} className="transition-transform duration-300 group-hover:rotate-90" />
          <span className="font-semibold tracking-wide">Add New City</span>
        </button>

        </div>

        {/* Search and Stats */}
        <div className="bg-gray-800 rounded-xl p-6 mb-8 shadow-lg">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
            <div className="w-full md:w-1/2">
              <label htmlFor="search" className="block text-sm font-medium text-gray-300 mb-2">
                Search Cities
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  id="search"
                  placeholder="Search by city, state or country..."
                  className="w-full bg-gray-700 border border-gray-600 text-white placeholder-gray-400 rounded-lg pl-10 pr-4 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
            <div className="bg-gray-700 rounded-lg px-4 py-3 shadow-inner">
              <p className="text-sm text-gray-300">Total Cities</p>
              <p className="text-2xl font-bold text-white">{cities.length}</p>
            </div>
          </div>
        </div>

        {/* Cities Table */}
        <div className="bg-gray-800 rounded-xl overflow-hidden shadow-lg">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-700">
              <thead className="bg-gray-700">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    City Name
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    State
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Country
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {loading ? (
                  <tr>
                    <td colSpan="4" className="px-6 py-8 text-center">
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-indigo-500"></div>
                      </div>
                    </td>
                  </tr>
                ) : filteredCities.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-6 py-8 text-center text-gray-400">
                      {search ? "No matching cities found" : "No cities available"}
                    </td>
                  </tr>
                ) : (
                  filteredCities.map((city) => (
                    <tr key={city.id} className="hover:bg-gray-750 transition-colors duration-150">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-white font-medium">{city.city}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-gray-300">{city.state}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-gray-300">{city.country}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => handleView({
                              id: city.id,
                              city: city.city,
                              state: city.state,
                              country: city.country
                            })}
                            className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-blue-400 hover:text-blue-300 transition-colors duration-200"
                            title="View Details"
                          >
                            <Eye size={18} />
                          </button>
                          <button
                            onClick={() => handleEdit({
                              id: city.id,
                              city: city.city,
                              state: city.state,
                              country: city.country
                            })}
                            className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-yellow-400 hover:text-yellow-300 transition-colors duration-200"
                            title="Edit"
                          >
                            <Edit2 size={18} />
                          </button>
                          <button
                            onClick={() => handleDelete(city.id)}
                            className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-red-400 hover:text-red-300 transition-colors duration-200"
                            title="Delete"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Empty State Illustration */}
        {!loading && filteredCities.length === 0 && (
          <div className="mt-12 text-center">
            <div className="mx-auto max-w-md">
              <svg
                className="mx-auto h-48 w-48 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1"
                  d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                ></path>
              </svg>
              <h3 className="mt-4 text-lg font-medium text-gray-300">No cities found</h3>
              <p className="mt-2 text-gray-500">
                {search
                  ? "Try adjusting your search to find what you're looking for."
                  : "Add your first city to get started."}
              </p>
              <button
                onClick={handleAdd}
                className="mt-6 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <Plus size={16} className="-ml-1 mr-2" />
                Add City
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CityDetailMain;

// CityDetailMain.js
// import React, { useState, useEffect } from 'react';
// import { Eye, Edit2, Trash2, Plus, Search } from 'lucide-react';
// import { useNavigate } from 'react-router-dom';
// import axiosInstance from '../../axiosInstance';

// function CityDetailMain() {
//   const [cities, setCities] = useState([
//     { id: 1, city: "Lucknow", state: "Uttar Pradesh", country: "India" },
//     { id: 2, city: "Patna", state: "Bihar", country: "India" },
//     { id: 3, city: "Delhi", state: "Delhi", country: "India" },
//     { id: 4, city: "Gorakhpur", state: "Uttar Pradesh", country: "India" },
//   ]);

//   const [loading, setLoading] = useState(false);
//   const [search, setSearch] = useState("");
//   const navigate = useNavigate();

//   useEffect(() => {
//     const fetchCities = async () => {
//       setLoading(true);
//       try {
//         // const response = await fetch("/citydetail/allcities");
//         const response = await axiosInstance.get("/citydetail/allcities");
//         // const result = await response.json();
//         const result = await response.data; // ✅ Correct
        

//         if (result?.data) {
//           const formattedCities = result.data.map((item, index) => ({
//             id: item._id || index + 1,
//             city: item.city.cityname,
//             state: item.city.state,
//             country: item.city.country,
//           }));

//           setCities(formattedCities);
//         }
//       } catch (error) {
//         console.error("Error fetching cities:", error);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchCities();
//   }, []);

//   const handleDelete = async (id) => {
//     try {
//       // Optional: Add API call to delete from backend
//       // await axiosInstance.delete(`/citydetail/delete/${id}`);
//       setCities(prev => prev.filter(city => city.id !== id));
//     } catch (error) {
//       console.error("Error deleting city:", error);
//     }
//   };

//   const handleEdit = ({ id, city, state, country }) => {
//     navigate(`/management/editCityDetail`, {
//       state: { cityname: city, statee: state, country, id }
//     });
//   };

//   const handleView = ({ id, city, state, country }) => {
//     navigate(`/management/viewCitydetail`, {
//       state: { cityname: city, statee: state, country, id }
//     });
//   };

//   const handleAdd = () => {
//     navigate('/management/mainpage', { state: { isAddingNewCity: true } });
//   };

//   const filteredCities = cities.filter(city =>
//     city.city.toLowerCase().includes(search.toLowerCase()) ||
//     city.state.toLowerCase().includes(search.toLowerCase()) ||
//     city.country.toLowerCase().includes(search.toLowerCase())
//   );

//   return (
//     <div className=" bg-gray-900 text-gray-100 p-6"style={{
//       overflowY: 'scroll',
//       height: '80vh',
//       width: '100%',
//       scrollbarWidth: 'px',
//       msOverflowStyle: 'none'
//     }}>
//       <div className="max-w-7xl mx-auto">
//         {/* Header Section */}
//         <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
//           <div>
//             <h1 className="text-3xl font-bold text-white mb-1">City Management</h1>
//             <p className="text-gray-400">Manage and view all city details</p>
//           </div>
//           <button
//             onClick={handleAdd}
//             className="mt-4 md:mt-0 flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg transition-all duration-200 shadow-lg hover:shadow-indigo-500/20"
//           >
//             <Plus size={18} />
//             Add New City
//           </button>
//         </div>

//         {/* Search and Stats */}
//         <div className="bg-gray-800 rounded-xl p-6 mb-8 shadow-lg">
//           <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
//             <div className="w-full md:w-1/2">
//               <label htmlFor="search" className="block text-sm font-medium text-gray-300 mb-2">
//                 Search Cities
//               </label>
//               <div className="relative">
//                 <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
//                   <Search className="h-5 w-5 text-gray-400" />
//                 </div>
//                 <input
//                   type="text"
//                   id="search"
//                   placeholder="Search by city, state or country..."
//                   className="w-full bg-gray-700 border border-gray-600 text-white placeholder-gray-400 rounded-lg pl-10 pr-4 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
//                   value={search}
//                   onChange={(e) => setSearch(e.target.value)}
//                 />
//               </div>
//             </div>
//             <div className="bg-gray-700 rounded-lg px-4 py-3 shadow-inner">
//               <p className="text-sm text-gray-300">Total Cities</p>
//               <p className="text-2xl font-bold text-white">{cities.length}</p>
//             </div>
//           </div>
//         </div>

//         {/* Cities Table */}
//         <div className="bg-gray-800 rounded-xl overflow-hidden shadow-lg">
//           <div className="overflow-x-auto">
//             <table className="min-w-full divide-y divide-gray-700">
//               <thead className="bg-gray-700">
//                 <tr>
//                   <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
//                     City Name
//                   </th>
//                   <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
//                     State
//                   </th>
//                   <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
//                     Country
//                   </th>
//                   <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-300 uppercase tracking-wider">
//                     Actions
//                   </th>
//                 </tr>
//               </thead>
//               <tbody className="divide-y divide-gray-700">
//                 {loading ? (
//                   <tr>
//                     <td colSpan="4" className="px-6 py-8 text-center">
//                       <div className="flex justify-center">
//                         <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-indigo-500"></div>
//                       </div>
//                     </td>
//                   </tr>
//                 ) : filteredCities.length === 0 ? (
//                   <tr>
//                     <td colSpan="4" className="px-6 py-8 text-center text-gray-400">
//                       {search ? "No matching cities found" : "No cities available"}
//                     </td>
//                   </tr>
//                 ) : (
//                   filteredCities.map((city) => (
//                     <tr key={city.id} className="hover:bg-gray-750 transition-colors duration-150">
//                       <td className="px-6 py-4 whitespace-nowrap">
//                         <div className="text-white font-medium">{city.city}</div>
//                       </td>
//                       <td className="px-6 py-4 whitespace-nowrap">
//                         <div className="text-gray-300">{city.state}</div>
//                       </td>
//                       <td className="px-6 py-4 whitespace-nowrap">
//                         <div className="text-gray-300">{city.country}</div>
//                       </td>
//                       <td className="px-6 py-4 whitespace-nowrap text-right">
//                         <div className="flex justify-end space-x-2">
//                           <button
//                             onClick={() => handleView({
//                               id: city.id,
//                               city: city.city,
//                               state: city.state,
//                               country: city.country
//                             })}
//                             className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-blue-400 hover:text-blue-300 transition-colors duration-200"
//                             title="View Details"
//                           >
//                             <Eye size={18} />
//                           </button>
//                           <button
//                             onClick={() => handleEdit({
//                               id: city.id,
//                               city: city.city,
//                               state: city.state,
//                               country: city.country
//                             })}
//                             className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-yellow-400 hover:text-yellow-300 transition-colors duration-200"
//                             title="Edit"
//                           >
//                             <Edit2 size={18} />
//                           </button>
//                           <button
//                             onClick={() => handleDelete(city.id)}
//                             className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-red-400 hover:text-red-300 transition-colors duration-200"
//                             title="Delete"
//                           >
//                             <Trash2 size={18} />
//                           </button>
//                         </div>
//                       </td>
//                     </tr>
//                   ))
//                 )}
//               </tbody>
//             </table>
//           </div>
//         </div>

//         {/* Empty State Illustration (hidden by default) */}
//         {!loading && filteredCities.length === 0 && (
//           <div className="mt-12 text-center">
//             <div className="mx-auto max-w-md">
//               <svg
//                 className="mx-auto h-48 w-48 text-gray-600"
//                 fill="none"
//                 stroke="currentColor"
//                 viewBox="0 0 24 24"
//                 xmlns="http://www.w3.org/2000/svg"
//               >
//                 <path
//                   strokeLinecap="round"
//                   strokeLinejoin="round"
//                   strokeWidth="1"
//                   d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
//                 ></path>
//               </svg>
//               <h3 className="mt-4 text-lg font-medium text-gray-300">No cities found</h3>
//               <p className="mt-2 text-gray-500">
//                 {search
//                   ? "Try adjusting your search to find what you're looking for."
//                   : "Add your first city to get started."}
//               </p>
//               <button
//                 onClick={handleAdd}
//                 className="mt-6 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
//               >
//                 <Plus size={16} className="-ml-1 mr-2" />
//                 Add City
//               </button>
//             </div>
//           </div>
//         )}
//       </div>
//     </div>
//   );
// }

// export default CityDetailMain;
// import React, { useState, useEffect } from 'react';
// import { Eye, Edit2, Trash2, Plus, Search } from 'lucide-react';
// import { useNavigate } from 'react-router-dom';
// import axiosInstance from '../../axiosInstance';

// function CityDetailMain() {
//   const [cities, setCities] = useState([
//     { id: 1, city: "Lucknow", state: "Uttar Pradesh", country: "India" },
//     { id: 2, city: "Patna", state: "Bihar", country: "India" },
//     { id: 3, city: "Delhi", state: "Delhi", country: "India" },
//     { id: 4, city: "Gorakhpur", state: "Uttar Pradesh", country: "India" },
//   ]);

//   const [loading, setLoading] = useState(false);
//   const [search, setSearch] = useState("");
//   const navigate = useNavigate();

//   useEffect(() => {
//     const fetchCities = async () => {
//       setLoading(true);
//       try {
//         const response = await fetch("/citydetail/allcities");
//         const result = await response.json();

//         if (result?.data) {
//           const formattedCities = result.data.map((item, index) => ({
//             id: item._id || index + 1,
//             city: item.city.cityname,
//             state: item.city.state,
//             country: item.city.country,
//           }));

//           setCities(formattedCities);
//         }
//       } catch (error) {
//         console.error("Error fetching cities:", error);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchCities();
//   }, []);

//   const handleDelete = async (id) => {
//     try {
//       // Optional: Add API call to delete from backend
//       // await axiosInstance.delete(`/citydetail/delete/${id}`);
//       setCities(prev => prev.filter(city => city.id !== id));
//     } catch (error) {
//       console.error("Error deleting city:", error);
//     }
//   };

//   const handleEdit = ({ id, city, state, country }) => {
//     navigate(`/management/editCityDetail`, {
//       state: { cityname: city, statee: state, country, id }
//     });
//   };

//   const handleView = ({ id, city, state, country }) => {
//     navigate(`/management/viewCitydetail`, {
//       state: { cityname: city, statee: state, country, id }
//     });
//   };

//   const handleAdd = () => {
//     navigate('/management/mainpage');
//   };

//   const filteredCities = cities.filter(city =>
//     city.city.toLowerCase().includes(search.toLowerCase()) ||
//     city.state.toLowerCase().includes(search.toLowerCase()) ||
//     city.country.toLowerCase().includes(search.toLowerCase())
//   );

//   return (
//     <div className=" bg-gray-900 text-gray-100 p-6"style={{
//       overflowY: 'scroll',
//       height: '80vh',
//       width: '100%',
//       scrollbarWidth: 'px',
//       msOverflowStyle: 'none'
//     }}>
//       <div className="max-w-7xl mx-auto">
//         {/* Header Section */}
//         <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
//           <div>
//             <h1 className="text-3xl font-bold text-white mb-1">City Management</h1>
//             <p className="text-gray-400">Manage and view all city details</p>
//           </div>
//           <button
//             onClick={handleAdd}
//             className="mt-4 md:mt-0 flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg transition-all duration-200 shadow-lg hover:shadow-indigo-500/20"
//           >
//             <Plus size={18} />
//             Add New City
//           </button>
//         </div>

//         {/* Search and Stats */}
//         <div className="bg-gray-800 rounded-xl p-6 mb-8 shadow-lg">
//           <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
//             <div className="w-full md:w-1/2">
//               <label htmlFor="search" className="block text-sm font-medium text-gray-300 mb-2">
//                 Search Cities
//               </label>
//               <div className="relative">
//                 <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
//                   <Search className="h-5 w-5 text-gray-400" />
//                 </div>
//                 <input
//                   type="text"
//                   id="search"
//                   placeholder="Search by city, state or country..."
//                   className="w-full bg-gray-700 border border-gray-600 text-white placeholder-gray-400 rounded-lg pl-10 pr-4 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
//                   value={search}
//                   onChange={(e) => setSearch(e.target.value)}
//                 />
//               </div>
//             </div>
//             <div className="bg-gray-700 rounded-lg px-4 py-3 shadow-inner">
//               <p className="text-sm text-gray-300">Total Cities</p>
//               <p className="text-2xl font-bold text-white">{cities.length}</p>
//             </div>
//           </div>
//         </div>

//         {/* Cities Table */}
//         <div className="bg-gray-800 rounded-xl overflow-hidden shadow-lg">
//           <div className="overflow-x-auto">
//             <table className="min-w-full divide-y divide-gray-700">
//               <thead className="bg-gray-700">
//                 <tr>
//                   <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
//                     City Name
//                   </th>
//                   <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
//                     State
//                   </th>
//                   <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
//                     Country
//                   </th>
//                   <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-300 uppercase tracking-wider">
//                     Actions
//                   </th>
//                 </tr>
//               </thead>
//               <tbody className="divide-y divide-gray-700">
//                 {loading ? (
//                   <tr>
//                     <td colSpan="4" className="px-6 py-8 text-center">
//                       <div className="flex justify-center">
//                         <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-indigo-500"></div>
//                       </div>
//                     </td>
//                   </tr>
//                 ) : filteredCities.length === 0 ? (
//                   <tr>
//                     <td colSpan="4" className="px-6 py-8 text-center text-gray-400">
//                       {search ? "No matching cities found" : "No cities available"}
//                     </td>
//                   </tr>
//                 ) : (
//                   filteredCities.map((city) => (
//                     <tr key={city.id} className="hover:bg-gray-750 transition-colors duration-150">
//                       <td className="px-6 py-4 whitespace-nowrap">
//                         <div className="text-white font-medium">{city.city}</div>
//                       </td>
//                       <td className="px-6 py-4 whitespace-nowrap">
//                         <div className="text-gray-300">{city.state}</div>
//                       </td>
//                       <td className="px-6 py-4 whitespace-nowrap">
//                         <div className="text-gray-300">{city.country}</div>
//                       </td>
//                       <td className="px-6 py-4 whitespace-nowrap text-right">
//                         <div className="flex justify-end space-x-2">
//                           <button
//                             onClick={() => handleView({
//                               id: city.id,
//                               city: city.city,
//                               state: city.state,
//                               country: city.country
//                             })}
//                             className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-blue-400 hover:text-blue-300 transition-colors duration-200"
//                             title="View Details"
//                           >
//                             <Eye size={18} />
//                           </button>
//                           <button
//                             onClick={() => handleEdit({
//                               id: city.id,
//                               city: city.city,
//                               state: city.state,
//                               country: city.country
//                             })}
//                             className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-yellow-400 hover:text-yellow-300 transition-colors duration-200"
//                             title="Edit"
//                           >
//                             <Edit2 size={18} />
//                           </button>
//                           <button
//                             onClick={() => handleDelete(city.id)}
//                             className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-red-400 hover:text-red-300 transition-colors duration-200"
//                             title="Delete"
//                           >
//                             <Trash2 size={18} />
//                           </button>
//                         </div>
//                       </td>
//                     </tr>
//                   ))
//                 )}
//               </tbody>
//             </table>
//           </div>
//         </div>

//         {/* Empty State Illustration (hidden by default) */}
//         {!loading && filteredCities.length === 0 && (
//           <div className="mt-12 text-center">
//             <div className="mx-auto max-w-md">
//               <svg
//                 className="mx-auto h-48 w-48 text-gray-600"
//                 fill="none"
//                 stroke="currentColor"
//                 viewBox="0 0 24 24"
//                 xmlns="http://www.w3.org/2000/svg"
//               >
//                 <path
//                   strokeLinecap="round"
//                   strokeLinejoin="round"
//                   strokeWidth="1"
//                   d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
//                 ></path>
//               </svg>
//               <h3 className="mt-4 text-lg font-medium text-gray-300">No cities found</h3>
//               <p className="mt-2 text-gray-500">
//                 {search
//                   ? "Try adjusting your search to find what you're looking for."
//                   : "Add your first city to get started."}
//               </p>
//               <button
//                 onClick={handleAdd}
//                 className="mt-6 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
//               >
//                 <Plus size={16} className="-ml-1 mr-2" />
//                 Add City
//               </button>
//             </div>
//           </div>
//         )}
//       </div>
//     </div>
//   );
// }

// export default CityDetailMain;
// import React, { useState , useEffect} from 'react';
// import { Eye } from 'lucide-react';
// import { useNavigate } from 'react-router-dom';
// import axiosInstance from '../../axiosInstance'

// function CityDetailMain() {
//   const [cities, setCities] = useState([
//     { id: 1, city: "Lucknow", state: "Uttar Pradesh", country: "India" },
//     { id: 2, city: "Patna", state: "Bihar", country: "India" },
//     { id: 3, city: "Delhi", state: "Delhi", country: "India" },
//     { id: 4, city: "Gorakhpur", state: "Uttar Pradesh", country: "India" },
//   ]);

//   // useEffect(()=>{
//   //   (async()=>{
//   //     try {
//   //      const response = await  axiosInstance.get('/citydetail/allcities');
//   //      console.log("sher 🦁 " , response?.data?.data)
//   //      setCities(response?.data?.data)
//   //     } catch (error) {
//   //       console.log(`Error`)
//   //     }
//   //   })()
//   // },[])

//   useEffect(() => {
//   const fetchCities = async () => {
//     try {
//       const response = await fetch("/citydetail/allcities");
//       const result = await response.json();

//       if (result?.data) {
//         const formattedCities = result.data.map((item, index) => ({
//           id: item._id || index + 1,
//           city: item.city.cityname,
//           state: item.city.state,
//           country: item.city.country,
//         }));

//         setCities(formattedCities);
//       }
//     } catch (error) {
//       console.error("Error fetching cities:", error);
//     }
//   };

//   fetchCities();
// }, []);



//   const [search, setSearch] = useState("");
//   const navigate = useNavigate();

//   const handleDelete = (id) => {
//     setCities(prev => prev.filter(city => city.id !== id));
//   };

//   const handleEdit = ({ id, city, state, country }) => {
//     const statee = state;
//     const cityname = city;
//     navigate(`/management/editCityDetail`, {
//       state: { cityname, statee, country, id }
//     });
//     // alert(`Edit functionality for city with ID: ${id}`);
//   };

//   const handleView = ({ id, city, state, country }) => {
//     const statee = state;
//     const cityname = city;
//     // console.log("Navigating with:", cityname, statee, country, id);
//     navigate(`/management/viewCitydetail`, {
//       state: { cityname, statee, country, id }
//     });
//   };

//   const handleAdd = () => {
//     navigate('/management/mainpage');
//   };

//   const filteredCities = cities.filter(city =>
//     city.city.toLowerCase().includes(search.toLowerCase()) ||
//     city.state.toLowerCase().includes(search.toLowerCase()) ||
//     city.country.toLowerCase().includes(search.toLowerCase())
//   );

//   return (
//     <div className="p-6 space-y-4 bg-gray-900 text-white" style={{
//       overflowY: 'scroll',
//       height: '80vh',
//       width: '100%',
//       scrollbarWidth: 'px',
//       msOverflowStyle: 'none'
//     }}>
//       <div className="flex justify-between items-center">
//         <h2 className="text-2xl font-semibold">City Details</h2>
//         <button
//           className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
//           onClick={handleAdd}
//         >
//           Add City Detail
//         </button>
//       </div>

//       <div className="w-full md:w-1/3">
//         <input
//           type="text"
//           placeholder="Search city, state or country..."
//           className="w-full bg-gray-800 border border-gray-600 text-white placeholder-gray-400 rounded-md px-3 py-2"
//           value={search}
//           onChange={(e) => setSearch(e.target.value)}
//         />
//       </div>

//       <div className="overflow-x-auto rounded-md border border-gray-700">
//         <table className="min-w-full divide-y divide-gray-700 bg-gray-800 text-white">
//           <thead className="bg-gray-700">
//             <tr>
//               <th className="px-4 py-2 text-left text-sm font-semibold">City Name</th>
//               <th className="px-4 py-2 text-left text-sm font-semibold">State</th>
//               <th className="px-4 py-2 text-left text-sm font-semibold">Country</th>
//               <th className="px-4 py-2 text-right text-sm font-semibold">Actions</th>
//             </tr>
//           </thead>
//           <tbody className="divide-y divide-gray-700">
//             {filteredCities.length === 0 ? (
//               <tr>
//                 <td colSpan="4" className="text-center px-4 py-4 text-gray-400">
//                   No matching records found.
//                 </td>
//               </tr>
//             ) : (
//               filteredCities.map(city => (
//                 <tr key={city.id} className="hover:bg-gray-700">
//                   <td className="px-4 py-2">{city.city}</td>
//                   <td className="px-4 py-2">{city.state}</td>
//                   <td className="px-4 py-2">{city.country}</td>
//                   <td className="px-4 py-2 text-right flex justify-end space-x-2">
//                     <button
//                       className="bg-green-600 hover:bg-green-700 text-white p-2 rounded-md"
//                       onClick={() => handleView({
//                         id: city.id,
//                         city: city.city,
//                         state: city.state,
//                         country: city.country
//                       })}
//                       title="View"
//                     >
//                       <Eye className="w-4 h-4" />
//                     </button>
//                     <button
//                       className="bg-yellow-500 hover:bg-yellow-600 text-black px-3 py-1 rounded-md"
//                       onClick={() => handleEdit({
//                         id: city.id,
//                         city: city.city,
//                         state: city.state,
//                         country: city.country
//                       })}
//                     >
//                       Edit
//                     </button>
//                     <button
//                       className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded-md"
//                       onClick={() => handleDelete(city.id)}
//                     >
//                       Delete
//                     </button>
//                   </td>
//                 </tr>
//               ))
//             )}
//           </tbody>
//         </table>
//       </div>
//     </div>
//   );
// }

// export default CityDetailMain;
